import { useMutation } from "@tanstack/react-query";
import React, { useState } from "react";
import { Controller } from "react-hook-form";

import { toast } from "@/components/toast/toast-queue";

import type {
  ResendCodeRequest,
  ResendCodeResponse,
} from "../api/kickstarter.api";

import { kickstarterApi } from "../api/kickstarter.api";
import {
  type EmailVerificationFormData,
  useEmailVerificationForm,
} from "../hooks/useEmailVerificationForm";

interface EmailVerificationStepProps {
  email: string;
  initialData: EmailVerificationFormData;
  isLoading?: boolean;
  onComplete: (data: EmailVerificationFormData) => void;
  onGoBack?: () => void;
  resendCodeFn?: (data: ResendCodeRequest) => Promise<ResendCodeResponse>;
}

function CodeInput({
  disabled = false,
  index,
  inputRef,
  onChange,
  onKeyDown,
  onPaste,
  value,
}: {
  disabled?: boolean;
  index: number;
  inputRef: React.RefObject<HTMLInputElement>;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent, index: number) => void;
  onPaste: (e: React.ClipboardEvent) => void;
  value: string;
}) {
  return (
    <input
      className={`w-12 h-16 text-2xl font-semibold text-center border-2 border-border-primary rounded-lg focus:border-utility-blue-500 focus:outline-none focus:ring-2 focus:ring-utility-blue-100 bg-bg-primary transition-all ${
        disabled ? "opacity-50 cursor-not-allowed bg-gray-100" : ""
      }`}
      disabled={disabled}
      inputMode="numeric"
      maxLength={1}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => onKeyDown(e, index)}
      onPaste={onPaste}
      pattern="[0-9]*"
      ref={inputRef}
      type="text"
      value={value}
    />
  );
}

export function EmailVerificationStep({
  email,
  initialData,
  onComplete,
  onGoBack: _onGoBack,
  resendCodeFn,
}: EmailVerificationStepProps) {
  const { control, handleSubmit, setValue, watch } =
    useEmailVerificationForm(initialData);
  const [codes, setCodes] = useState<string[]>(["", "", "", "", "", ""]);
  const [isValidating, setIsValidating] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = [
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
  ];

  const verificationCode = watch("verificationCode");

  // Timer effect for resend countdown
  React.useEffect(() => {
    let interval: NodeJS.Timeout;

    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  React.useEffect(() => {
    if (verificationCode) {
      const digits = [...verificationCode];

      setCodes([
        ...digits,
        ...Array.from({ length: 6 - digits.length }, () => ""),
      ]);
    }
  }, [verificationCode]);

  const handleCodeChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value) || isValidating) return;

    const newCodes = [...codes];

    newCodes[index] = value;
    setCodes(newCodes);

    const fullCode = newCodes.join("");

    setValue("verificationCode", fullCode);

    if (value && index < 5) {
      inputRefs[index + 1]?.current?.focus();
    }

    if (fullCode.length === 6 && /^\d{6}$/.test(fullCode)) {
      setIsValidating(true);
      setTimeout(() => {
        onComplete({ verificationCode: fullCode });
        setIsValidating(false);
      }, 1500);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Backspace" && !codes[index] && index > 0) {
      inputRefs[index - 1]?.current?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    if (isValidating) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, 6);

    if (!/^\d+$/.test(pastedData)) return;

    const digits = [...pastedData];
    const newCodes = [
      ...digits,
      ...Array.from({ length: 6 - digits.length }, () => ""),
    ];

    setCodes(newCodes);
    setValue("verificationCode", pastedData);

    const nextIndex = Math.min(digits.length, 5);

    inputRefs[nextIndex]?.current?.focus();

    if (pastedData.length === 6) {
      setIsValidating(true);
      setTimeout(() => {
        onComplete({ verificationCode: pastedData });
        setIsValidating(false);
      }, 1500);
    }
  };

  const onSubmit = (data: EmailVerificationFormData) => {
    onComplete(data);
  };

  const resendCodeMutation = useMutation({
    mutationFn: (email: string) => {
      const resendFn = resendCodeFn || kickstarterApi.resendCode;

      return resendFn({ email });
    },
    onError: (error: Error) => {
      toast.add(
        {
          description: error.message || "Please try again",
          title: "Failed to resend code",
          type: "error",
        },
        {
          timeout: 5000,
        },
      );
    },
    onSuccess: () => {
      setResendTimer(60); // Start 60 second countdown
      toast.add(
        {
          description: "Please check your email",
          title: "Code sent successfully",
          type: "success",
        },
        {
          timeout: 5000,
        },
      );
    },
  });

  const handleResendCode = () => {
    if (resendTimer > 0 || resendCodeMutation.isPending) return;
    resendCodeMutation.mutate(email);
  };

  return (
    <div className="flex flex-col gap-6">
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <div className="flex justify-center gap-3">
            {codes.map((code, index) => (
              <CodeInput
                disabled={isValidating}
                index={index}
                inputRef={inputRefs[index]!}
                key={index}
                onChange={(value) => handleCodeChange(value, index)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                value={code}
              />
            ))}
          </div>

          <Controller
            control={control}
            name="verificationCode"
            render={({ fieldState: { error } }) => (
              <div className="text-center">
                {isValidating && (
                  <div className="text-sm text-utility-blue-500 font-medium mb-2">
                    Validating code...
                  </div>
                )}
                {error && (
                  <div className="text-sm text-text-error-primary">
                    {error.message}
                  </div>
                )}
              </div>
            )}
          />
        </div>

        <div className="text-center">
          <span className="text-sm text-text-secondary">
            Didn&apos;t get a code?{" "}
            <button
              className={`text-sm font-medium underline transition-all ${
                resendTimer > 0 || resendCodeMutation.isPending || isValidating
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-bg-brand-solid cursor-pointer hover:text-bg-brand-solid-hover"
              }`}
              disabled={
                resendTimer > 0 || resendCodeMutation.isPending || isValidating
              }
              onClick={handleResendCode}
              type="button"
            >
              {resendCodeMutation.isPending
                ? "Sending..."
                : resendTimer > 0
                  ? `Resend in ${resendTimer}s`
                  : "Click to resend."}
            </button>
          </span>
        </div>
      </form>
    </div>
  );
}
